import { FC, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useProcessTranscriptFileMutation, type TranscriptFile } from '@teachur/http-client';
import { TranscriptFilesList } from '@new/features/transcripts/shared/components/TranscriptFilesList';

interface LegacyDocumentsListProps {
  transcriptId: number;
  files: TranscriptFile[];
  onOpenTranscript?: (transcriptId: number) => void;
}

export const LegacyDocumentsList: FC<LegacyDocumentsListProps> = ({
  transcriptId,
  files,
  onOpenTranscript,
}) => {
  const processFile = useProcessTranscriptFileMutation();
  const [extractingFileId, setExtractingFileId] = useState<number | null>(null);

  const onExtract = async (file: TranscriptFile) => {
    setExtractingFileId(file.id);
    try {
      const created = await processFile.mutateAsync({
        transcriptId,
        transcriptFileId: file.id,
        body: { title: file.label },
      });
      toast.success(`Transcript "${created.institution}" created`);
      onOpenTranscript?.(created.id);
    } catch {
      toast.error('Failed to extract document');
    } finally {
      setExtractingFileId(null);
    }
  };

  const renderActions = (file: TranscriptFile) => {
    const extracting = extractingFileId === file.id;
    return (
      <button
        type="button"
        disabled={extractingFileId != null}
        onClick={() => onExtract(file)}
        className="cursor-pointer [font-size:var(--fs-l5)]! font-medium text-(--primary-O900)! underline disabled:cursor-not-allowed disabled:opacity-50"
      >
        {extracting ? (
          <span className="inline-flex items-center gap-1">
            <Loader2 className="size-3 animate-spin" />
            Extracting…
          </span>
        ) : (
          'Extract'
        )}
      </button>
    );
  };

  return <TranscriptFilesList files={files} renderActions={renderActions} />;
};
